import { Select as SelectPrimitive, type SelectRootItemComponentProps } from "@kobalte/core/select";
import { Check, ChevronsUpDown, Plus } from "@fleet/icons";
import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@jornada/shared";

import { overlayPortalHost } from "./overlay-portal";

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true.*/
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph.*/
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No sites yet").*/
  description?: string;
}

/** Single option row. Module-level so it is not redefined on every render.*/
function SelectItem(props: SelectRootItemComponentProps<SelectOption>) {
  return (
    <SelectPrimitive.Item
      item={props.item}
      class={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-md py-2 pl-3 pr-8 text-sm outline-none",
        "transition-colors duration-150",
        "focus:bg-accent focus:text-accent-foreground",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      )}
    >
      <SelectPrimitive.ItemLabel>{props.item.rawValue.label}</SelectPrimitive.ItemLabel>
      <SelectPrimitive.ItemIndicator class="absolute right-2 flex h-4 w-4 items-center justify-center">
        <Check class="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

interface SelectProps {
  /** Currently selected value (empty string means "no selection").*/
  value: string;
  /** Fired with the new value; empty string is passed for the empty option.*/
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Placeholder shown when no value is selected.*/
  placeholder?: string;
  /** Accessible label when there is no visible <Label> (e.g. toolbar filters).*/
  "aria-label"?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  class?: string;
  /** Compact variant for inline toolbars/filters.*/
  size?: "sm" | "md";
  "data-testid"?: string;
  /**
   * Shown inside the dropdown when `options` is empty. Lets the user jump to the
   * place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message shown in the empty dropdown when there is no (permitted) CTA.*/
  emptyLabel?: string;
}

/**
 * Premium Select built on @kobalte/core, matching the input-style trigger,
 * theme tokens and animated panel used across the app. Drop-in replacement for
 * native <select>: pass string `value`/`onChange`/`options` and it preserves
 * keyboard, typeahead and ARIA behaviour. Theme-safe (no hardcoded colors).
 */
export function Select(props: SelectProps): JSX.Element {
  const [local, others] = splitProps(props, [
    "value",
    "onChange",
    "options",
    "placeholder",
    "aria-label",
    "id",
    "name",
    "disabled",
    "class",
    "size",
    "data-testid",
    "emptyAction",
    "emptyLabel",
  ]);

  const selectedOption = () => local.options.find((o) => o.value === local.value) ?? null;
  const isEmpty = () => local.options.length === 0;

  return (
    <SelectPrimitive<SelectOption>
      options={local.options}
      optionValue="value"
      optionTextValue="label"
      optionDisabled="disabled"
      value={selectedOption()}
      // Required single-select: re-clicking the ALREADY selected option must NOT
      // toggle it to empty. By default Kobalte emits `null` when re-clicking the
      // current selection (deselect) → here it coerced to "" and cleared the field before
      // submit (e.g. re-choosing the same organization/template). With
      // `disallowEmptySelection` the value can be CHANGED to another option but not
      // emptied by interaction. (CC: Select re-click deselects.)
      disallowEmptySelection
      onChange={(option) => local.onChange(option?.value ?? "")}
      disabled={local.disabled}
      name={local.name}
      placeholder={local.placeholder}
      itemComponent={SelectItem}
    >
      <SelectPrimitive.Trigger
        id={local.id}
        aria-label={local["aria-label"]}
        data-testid={local["data-testid"]}
        class={cn(
          "flex w-full items-center justify-between gap-2 rounded-md border border-input bg-background text-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
          local.size === "sm" ? "h-9 px-3 py-1 font-bold" : "h-10 px-3 py-2",
          local.class,
        )}
        {...others}
      >
        <SelectPrimitive.Value<SelectOption> class="block truncate text-left">
          {(state) => (
            <Show
              when={state.selectedOption()}
              fallback={<span class="text-muted-foreground">{local.placeholder}</span>}
            >
              {state.selectedOption()?.label}
            </Show>
          )}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon class="shrink-0 opacity-60">
          <ChevronsUpDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      {/* mount: host tagged data-live-announcer para que ariaHideOutside del
          Dialog modal NO oculte el listbox (CC-21) — ver overlay-portal.ts. */}
      <SelectPrimitive.Portal mount={overlayPortalHost()}>
        <SelectPrimitive.Content
          class={cn(
            // z-[250]: ABOVE the Dialog (overlay z-[200], content z-[210]);
            // with z-50 the listbox sat visually BELOW the modal (CC-21).
            "relative z-[250] min-w-[var(--kb-popper-anchor-width)] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md outline-none",
            "ui-select-content",
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-60 overflow-y-auto overflow-x-hidden p-1" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted "no options" message. The CTA closes the dropdown on activation.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-2.5 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when no label is given. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1">
          <Show when={action().description}>
            <p class="px-2.5 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
